import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Banknote, Sun, PiggyBank, Coins, Download, Briefcase, Users } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { fmtCurrency, PROGRAMME_COLORS, fileBase, fmtAxisMoney } from "@/lib/format";
import { useAuth } from "@/auth/AuthContext";
import { TermFilter } from "@/components/TermFilter";

const pct = (r) => `${+(r * 100).toFixed(2)}%`;

const WKPI = ({ label, value, icon: Icon, color, delay, testId }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    data-testid={testId}
    className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 hover:border-[var(--hp-border-hi)] transition-colors duration-300">
    <div className="flex items-start justify-between gap-2">
      <p className="min-w-0 text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)] [overflow-wrap:anywhere]">{label}</p>
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl" style={{ background: `${color}22`, color }}>
        <Icon className="h-[18px] w-[18px]" />
      </div>
    </div>
    <p className="font-mono-num text-xl lg:text-[26px] font-bold text-[var(--hp-heading)] mt-4 tracking-tight [overflow-wrap:anywhere]">{value}</p>
  </motion.div>
);

export default function Wages({ api, year }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [term, setTerm] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/wages`, { params: { year, ...(term ? { term } : {}) } });
      setData(res.data);
    } catch (e) { console.error(e); }
  }, [api, year, term]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals || { base: 0, holiday: 0, kiwiSaver: 0, total: 0, floorTotal: 0, adminTotal: 0, floorHours: 0, adminHours: 0 };
  const rates = data?.rates || { holidayPay: 0.08, kiwiSaver: 0.03 };
  const monthly = data?.monthly || [];
  const byProg = (data?.byProgramme || []).filter((p) => p.total > 0);
  const byStaff = data?.byStaff || [];
  const hasData = t.total > 0;

  const exportExcel = async () => {
    try {
      const res = await axios.get(`${api}/wages/export`, { params: { year, ...(term ? { term } : {}) }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${fileBase(user?.tenant_name)}_Wages_${year}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Wages Excel exported");
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5">
        <div>
          <h2 className="font-head text-2xl font-bold text-[var(--hp-heading)]">Wages & Payroll Trends</h2>
          <p className="text-sm text-[var(--hp-muted)] mt-2">Floor wages from activities plus admin hours, with Holiday Pay ({pct(rates.holidayPay)}) and KiwiSaver ({pct(rates.kiwiSaver)}).</p>
          <p className="text-xs text-[var(--hp-muted)] mt-1.5 max-w-2xl" data-testid="wages-basis-note">These figures come from the Activity Log (roster pushes and hand-entered activity wages). Forecast vs Actual and the Term report use <b>confirmed kiosk timesheets</b> once a month is confirmed (Team → Timesheets), so after confirming a month the two can differ — hours actually worked versus hours planned.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <TermFilter value={term} onChange={setTerm} idPrefix="wages-" />
          <button data-testid="wages-export-button" onClick={exportExcel}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--hp-primary)] px-4 py-2.5 text-sm font-semibold text-[#04231a] hover:bg-[var(--hp-primary-hover)] transition-colors duration-200">
            <Download className="h-4 w-4" /> Export Wages Excel
          </button>
        </div>
      </div>

      <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(10.5rem,1fr))]">
        <WKPI label="Total Wages" value={fmtCurrency(t.total)} icon={Banknote} color="#34D399" delay={0.02} testId="wages-kpi-total" />
        <WKPI label={`Floor · ${t.floorHours}h`} value={fmtCurrency(t.floorTotal)} icon={Users} color="#22D3EE" delay={0.05} testId="wages-kpi-floor" />
        <WKPI label={`Admin · ${t.adminHours}h`} value={fmtCurrency(t.adminTotal)} icon={Briefcase} color="#F472B6" delay={0.08} testId="wages-kpi-admin" />
        <WKPI label="Base Wages" value={fmtCurrency(t.base)} icon={Coins} color="#3B82F6" delay={0.11} testId="wages-kpi-base" />
        <WKPI label={`Holiday Pay (${pct(rates.holidayPay)})`} value={fmtCurrency(t.holiday)} icon={Sun} color="#F59E0B" delay={0.14} testId="wages-kpi-holiday" />
        <WKPI label={`KiwiSaver (${pct(rates.kiwiSaver)})`} value={fmtCurrency(t.kiwiSaver)} icon={PiggyBank} color="#A78BFA" delay={0.17} testId="wages-kpi-kiwisaver" />
      </div>

      <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 sm:p-6" data-testid="wages-trend-chart">
        <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)] mb-5">Monthly Wages Breakdown</h3>
        {!hasData ? (
          <div className="grid place-items-center py-14 text-center">
            <Banknote className="h-9 w-9 text-[var(--hp-muted)] mb-3" />
            <p className="text-sm text-[var(--hp-muted)] max-w-sm">
              No wages recorded yet. Log activities with base wages in the Data Input tab and they'll roll up here — holiday pay and KiwiSaver are calculated automatically.
            </p>
          </div>
        ) : (
          <div className="h-[340px] w-full">
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 360, height: 240 }}>
              <BarChart data={monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                <XAxis dataKey="month" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => fmtAxisMoney(v)} />
                <Tooltip contentStyle={{ background: "#0B1120", border: "1px solid #334155", borderRadius: 12, fontSize: 13 }}
                  labelStyle={{ color: "#E2E8F0", fontWeight: 700 }} formatter={(val, name) => [fmtCurrency(val), name]} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Bar dataKey="base" name="Floor Base" stackId="w" fill="#3B82F6" maxBarSize={30} />
                <Bar dataKey="holiday" name="Floor Holiday Pay" stackId="w" fill="#F59E0B" maxBarSize={30} />
                <Bar dataKey="kiwiSaver" name="Floor KiwiSaver" stackId="w" fill="#A78BFA" maxBarSize={30} />
                <Bar dataKey="admin" name="Admin Wages" stackId="w" fill="#F472B6" radius={[4, 4, 0, 0]} maxBarSize={30} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {byStaff.length > 0 && (
        <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] overflow-hidden" data-testid="wages-by-staff">
          <div className="px-6 py-4 border-b border-[var(--hp-border)]">
            <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">Wages by Staff Member</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[var(--hp-muted)] border-b border-[var(--hp-border)]">
                  <th className="px-6 py-3 font-semibold">Staff</th>
                  <th className="px-6 py-3 font-semibold text-right">Floor Hrs</th>
                  <th className="px-6 py-3 font-semibold text-right">Admin Hrs</th>
                  <th className="px-6 py-3 font-semibold text-right">Base</th>
                  <th className="px-6 py-3 font-semibold text-right">Holiday Pay</th>
                  <th className="px-6 py-3 font-semibold text-right">KiwiSaver</th>
                  <th className="px-6 py-3 font-semibold text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {byStaff.map((s) => (
                  <tr key={s.id} data-testid={`wages-staff-row-${s.id}`} className="border-b border-[var(--hp-border)]/60 hover:bg-[var(--hp-tint-3)] transition-colors duration-150">
                    <td className="px-6 py-3.5 font-semibold text-[var(--hp-heading)]">{s.name}</td>
                    <td className="px-6 py-3.5 text-right font-mono-num text-[var(--hp-c-cyan)]">{s.floorHours}h</td>
                    <td className="px-6 py-3.5 text-right font-mono-num text-[var(--hp-c-pink-strong)]">{s.adminHours}h</td>
                    <td className="px-6 py-3.5 text-right font-mono-num text-[var(--hp-text)]">{fmtCurrency(s.base)}</td>
                    <td className="px-6 py-3.5 text-right font-mono-num text-[var(--hp-gold)]">{fmtCurrency(s.holiday)}</td>
                    <td className="px-6 py-3.5 text-right font-mono-num text-[var(--hp-c-violet)]">{fmtCurrency(s.kiwiSaver)}</td>
                    <td className="px-6 py-3.5 text-right font-mono-num font-bold text-[var(--hp-profit)]">{fmtCurrency(s.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {byProg.length > 0 && (
        <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] overflow-hidden" data-testid="wages-by-programme">
          <div className="px-6 py-4 border-b border-[var(--hp-border)]">
            <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">Wages by Programme</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[var(--hp-muted)] border-b border-[var(--hp-border)]">
                  <th className="px-6 py-3 font-semibold">Programme</th>
                  <th className="px-6 py-3 font-semibold text-right">Hours</th>
                  <th className="px-6 py-3 font-semibold text-right">Base Wages</th>
                  <th className="px-6 py-3 font-semibold text-right">Holiday Pay</th>
                  <th className="px-6 py-3 font-semibold text-right">KiwiSaver</th>
                  <th className="px-6 py-3 font-semibold text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {byProg.map((p) => (
                  <tr key={p.name} className="border-b border-[var(--hp-border)]/60 hover:bg-[var(--hp-tint-3)] transition-colors duration-150">
                    <td className="px-6 py-3.5 font-semibold text-[var(--hp-heading)]">
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: PROGRAMME_COLORS[p.name] }} />
                        {p.name}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 text-right font-mono-num text-[var(--hp-c-cyan)]">{p.hours}h</td>
                    <td className="px-6 py-3.5 text-right font-mono-num text-[var(--hp-text)]">{fmtCurrency(p.base)}</td>
                    <td className="px-6 py-3.5 text-right font-mono-num text-[var(--hp-gold)]">{fmtCurrency(p.holiday)}</td>
                    <td className="px-6 py-3.5 text-right font-mono-num text-[var(--hp-c-violet)]">{fmtCurrency(p.kiwiSaver)}</td>
                    <td className="px-6 py-3.5 text-right font-mono-num font-bold text-[var(--hp-profit)]">{fmtCurrency(p.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
